import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { formatDate, useAnalyticsPageView, usePathParam } from '@chilecompra/react-kit'
import { Skeleton, Table, TableRow, Toast, Typography } from '@chilecompra/react-kit/components'
import { checkIfForeignDni } from '../../modules/utils/helpers'
import { onGetSiiInformationThunk } from './SiiInformationSection.actions'
import PrivateSection from '../../components/Security/PrivateSection'
import PublicSection from '../../components/Security/PublicSection'
import { Content, TableCell } from './SiiInformationSection.styles'

/**
 * The SII information section container.
 */
const SiiInformationSection = () => {
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const date = formatDate(new Date(), 'dd/MM/yyyy')
  const isForeign = checkIfForeignDni(dni)

  const { businessActivities, error, loading } = useSelector(state => state.siiInformation)
  const { error: summaryError } = useSelector(state => state.summarySection)

  useEffect(() => {
    if (!loading || isForeign) {
      return
    }

    dispatch(onGetSiiInformationThunk({ dni }))
  }, [isForeign])

  useAnalyticsPageView('/ficha/informacion-general')

  return isForeign ? null : (
    <>
      <PublicSection>
        <TableRow>
          <TableCell enableHead padding="12px 9px 16px 0" width="142px">
            Actividades económicas de la empresa
            <Typography variant="subtitle">Fuente: Servicio de Impuestos Internos</Typography>
          </TableCell>

          {loading && (
            <TableCell padding="12px 9px 16px 0">
              <Skeleton height="30px" variant="rect" width="100%" />
            </TableCell>
          )}

          {!loading && !error && businessActivities !== undefined && (
            <TableCell>
              <Table accessibility={{ label: 'Actividades económicas' }}>
                {businessActivities.map(businessActivity => {
                  return (
                    <TableRow key={businessActivity.code}>
                      <TableCell disableBottomBorder padding="0">
                        <Typography variant="body2" color="black2" padding="0 6px 0 0">
                          {businessActivity.code}
                        </Typography>
                      </TableCell>
                      <TableCell disableBottomBorder padding="0">
                        {businessActivity.description}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </Table>
            </TableCell>
          )}

          {!loading && error && (
            <TableCell>
              <Toast
                disableBackground
                severity="default"
                title="Lo sentimos, estamos presentando problemas con nuestro servidor."
              >
                En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos
                minutos más.
              </Toast>
            </TableCell>
          )}
        </TableRow>
      </PublicSection>

      {summaryError?.reason === 'FORBIDDEN_ERROR' && (
        <PrivateSection>
          <Content>
            <Typography variant="h4" fontWeight="bold">
              Actividades económicas de la empresa proveedora
            </Typography>
            <Typography variant="subtitle" padding="0 0 16px">
              {loading && <Skeleton width="40%" />}
              {!loading &&
                !error &&
                `Fuente: Servicio de Impuestos Internos. Información actualizada con fecha ${date}.`}
            </Typography>

            {loading && <Skeleton height="30px" variant="rect" width="100%" />}

            {!loading && !error && (
              <Table accessibility={{ label: 'Actividades económicas' }}>
                {businessActivities.map(businessActivity => {
                  return (
                    <TableRow key={businessActivity.code}>
                      <TableCell disableBottomBorder padding="0">
                        <Typography variant="body2" color="black2" padding="0 6px 0 0">
                          {businessActivity.code}
                        </Typography>
                      </TableCell>
                      <TableCell disableBottomBorder padding="0">
                        {businessActivity.description}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </Table>
            )}

            {!loading && !error && businessActivities?.length === 0 && (
              <Typography color="black3" variant="body2">
                No hay información disponible.
              </Typography>
            )}

            {!loading && error && (
              <Toast
                disableBackground
                severity="default"
                title="Lo sentimos, estamos presentando problemas con nuestro servidor."
              >
                En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos
                minutos más.
              </Toast>
            )}
          </Content>
        </PrivateSection>
      )}
    </>
  )
}

export default SiiInformationSection
